/* eslint-disable no-nested-ternary */
import { useEffect, useRef } from "react";

import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faExclamationCircle,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ActivityCard } from "routes/patients/views/Treatment/components/TreatmentEditor/ActivityCard";
import type { onPinExercise } from "routes/patients/views/Treatment/components/TreatmentEditor/ActivityCard/ActivityCard";
import { Notification } from "shared/atoms/Notification";

import { DropZoneIndicator } from "../shared";
import {
  ActivityCount,
  ContentContainer,
  ContentContainerHeader,
  ContentContainerHeaderLabel,
  ContentContainerHeaderLeft,
  ContentDropZoneContainer,
} from "../styles";
import type { HandleLevelChange, HandleSetsAndRepsChange, TimelineActivity, TimelineWeek } from "../types";

interface WeekRowProps {
  currentWeek: number;
  exercisesPerDay?: number;
  isCurrent: boolean;
  isDraggingFromLibrary: boolean;
  isMultiSelectMode: boolean;
  isPastWeek?: boolean;
  noExercisesError: boolean;
  selectedActivities: Set<string>;
  totalPinnedCount?: number;
  warning?: string;
  week: TimelineWeek;
  onActivityClick: (activity: TimelineActivity) => void;
  onCopyActivity: (activity: TimelineActivity) => void;
  onCopySelected: (weekNumber: number) => void;
  onDismissWarning: (weekNumber: number) => void;
  onLevelChange?: HandleLevelChange;
  onPinExercise?: onPinExercise;
  onPreview: (id: number, part: number) => void;
  onRemoveActivity: (activity: TimelineActivity, weekNumber: number) => void;
  onToggleExpanded: (weekNumber: number) => void;
  onToggleLevelLock?: HandleLevelChange;
  onSetsAndRepsChange?: HandleSetsAndRepsChange;
}

export const WeekRow: React.FC<WeekRowProps> = ({
  currentWeek,
  exercisesPerDay,
  isCurrent,
  isDraggingFromLibrary,
  isMultiSelectMode,
  isPastWeek,
  noExercisesError,
  selectedActivities,
  totalPinnedCount,
  warning,
  week,
  onActivityClick,
  onCopyActivity,
  onCopySelected,
  onDismissWarning,
  onLevelChange,
  onPinExercise,
  onPreview,
  onRemoveActivity,
  onToggleExpanded,
  onToggleLevelLock,
  onSetsAndRepsChange,
}) => {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    id: `week-${week.weekNumber}`,
    data: {
      droppableType: "week",
      weekNumber: week.weekNumber,
    },
    disabled: isPastWeek,
  });

  const { weekNumber } = week;
  const wasAutoExpandedRef = useRef(false);
  const activitiesCountRef = useRef(week.activities.length);

  // Auto-expand/collapse week when dragging over/away from it
  useEffect(() => {
    if (isOver && !week.expanded) {
      // Auto-expand when dragging over a collapsed week
      onToggleExpanded(week.weekNumber);
      wasAutoExpandedRef.current = true;
    } else if (!isOver && wasAutoExpandedRef.current && week.expanded) {
      // Check if activities count changed (item was dropped)
      if (week.activities.length !== activitiesCountRef.current) {
        // Item was dropped, keep expanded and reset auto-expand flag
        wasAutoExpandedRef.current = false;
        activitiesCountRef.current = week.activities.length;
      } else {
        // No drop happened, auto-collapse
        onToggleExpanded(week.weekNumber);
        wasAutoExpandedRef.current = false;
      }
    }
  }, [isOver, week.expanded, week.weekNumber, week.activities.length, onToggleExpanded]);

  const uniqueActivities = Array.from(new Map(week.activities.map(activity => [activity.id, activity])).values());
  const selectedInThisWeek = uniqueActivities.filter(a => selectedActivities.has(a.id));
  const hasSelectedActivities = selectedInThisWeek.length > 0;
  const showError = noExercisesError && weekNumber === 1;

  return (
    <>
      {showError && (
        <Notification variant="danger" style={{ marginBottom: "12px" }}>
          {t("treatment.editor.feedback.errors.at_least_one_exercise")}
        </Notification>
      )}
      <ContentContainer
        ref={week.expanded ? undefined : setNodeRef}
        className={clsx({ "is-error": showError, "is-over": isOver, "is-past": isPastWeek })}
      >
        <ContentContainerHeader
          className={clsx({ "is-clickable": true, "is-current": isCurrent, "is-past": isPastWeek })}
        >
          <ContentContainerHeaderLeft onClick={() => onToggleExpanded(week.weekNumber)}>
            <ExpandIcon>
              <FontAwesomeIcon icon={week.expanded ? faChevronDown : faChevronRight} size="sm" />
            </ExpandIcon>
            <ContentContainerHeaderLabel>
              {t("common.week_num", { number: week.weekNumber })}
              {isCurrent && <CurrentBadge>{t("common.current")}</CurrentBadge>}
            </ContentContainerHeaderLabel>
            <ActivityCount>{t("common.activities", { count: uniqueActivities.length })}</ActivityCount>
          </ContentContainerHeaderLeft>
          {hasSelectedActivities && (
            <CopySelectedButton
              onClick={e => {
                e.stopPropagation();
                onCopySelected(week.weekNumber);
              }}
              title={t("treatment.editor.copy_exercise.copy_selected", { count: selectedInThisWeek.length })}
            >
              <FontAwesomeIcon icon={faCopy} />
              {t("treatment.editor.copy_exercise.copy_selected", { count: selectedInThisWeek.length })}
            </CopySelectedButton>
          )}
        </ContentContainerHeader>

        {warning && (
          <WarningBanner>
            <WarningContent>
              <WarningIcon>
                <FontAwesomeIcon icon={faExclamationCircle} />
              </WarningIcon>
              <WarningText>{warning}</WarningText>
            </WarningContent>
            <DismissButton onClick={() => onDismissWarning(week.weekNumber)}>
              <FontAwesomeIcon icon={faTimes} />
            </DismissButton>
          </WarningBanner>
        )}

        {week.expanded && (
          <ContentDropZoneContainer ref={setNodeRef} className={clsx({ "is-error": showError, "is-over": isOver })}>
            <SortableContext items={uniqueActivities.map(a => a.id)} strategy={verticalListSortingStrategy}>
              {uniqueActivities.length === 0 ? (
                <DropZoneIndicator activityType="exercises" />
              ) : (
                <WeekActivitiesList $isDraggingFromLibrary={isDraggingFromLibrary}>
                  {uniqueActivities.map(activity => (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      currentWeek={currentWeek}
                      exercisesPerDay={exercisesPerDay}
                      isMultiSelectMode={isMultiSelectMode}
                      isSelected={selectedActivities.has(activity.id)}
                      totalPinnedCount={totalPinnedCount}
                      weekNumber={weekNumber}
                      onClick={onActivityClick}
                      onCopy={onCopyActivity}
                      onLevelChange={onLevelChange}
                      onPinExercise={onPinExercise}
                      onPreview={onPreview}
                      onRemove={onRemoveActivity}
                      onToggleLevelLock={onToggleLevelLock}
                      onSetsAndRepsChange={onSetsAndRepsChange}
                    />
                  ))}
                </WeekActivitiesList>
              )}
            </SortableContext>
          </ContentDropZoneContainer>
        )}
      </ContentContainer>
    </>
  );
};

const CopySelectedButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #0066cc;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #0052a3;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 102, 204, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 14px;
  }
`;

const ExpandIcon = styled.div`
  color: #5c6f82;
  display: flex;
  align-items: center;
`;

export const CurrentBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  background-color: #0066cc;
  color: #ffffff;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background-color: #e3f2fd;
  border-top: 1px solid #90caf9;
  border-bottom: 1px solid #90caf9;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const WarningContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  color: #1976d2;
  font-size: 16px;
`;

export const WarningText = styled.span`
  font-size: 14px;
  color: #1565c0;
  font-weight: 500;
`;

export const DismissButton = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    background-color: #bbdefb;
    color: #0d47a1;
  }

  svg {
    font-size: 14px;
  }
`;

const WeekActivitiesList = styled.div<{ $isDraggingFromLibrary: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  row-gap: 12px;
  column-gap: 16px;
  pointer-events: ${({ $isDraggingFromLibrary }) => ($isDraggingFromLibrary ? "none" : "auto")};
  justify-content: start;
  width: 100%;
  box-sizing: border-box;
  align-items: start;
`;
